import { useMemo, useRef, useState, type JSX } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import QuillResizeImage from 'quill-resize-image';

import 'react-quill-new/dist/quill.snow.css';

import ModalCompanyImageGallery from 'components/modal/ModalCompanyImageGallery';

import QuillModalButton from './parts/QuillModalButton';
import { getCompanyImageUrl, getUid } from 'utils/app';

// --- HMR 二重登録防止フラグ ---
// Vite HMR でモジュールが再評価されても Quill.register が 2 度走らないようにする
let isQuillModulesRegistered = false;

// Quill.import('blots/embed') の戻り型は react-quill-new / quill ともに
// 公開された Blot 基底型の export がなく、コミュニティでも any 経由で
// extends するパターンが事実上の慣行。行単位で eslint-disable-next-line を付与する。
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Embed = Quill.import('blots/embed') as any;

// parchment 3 の StyleAttributor を使い、img の inline style（float / margin / display）を
// Quill の Delta に保持するためのフォーマットを生成する。
// Quill 2 のビルトイン ImageBlot は width / height 属性は保持するが style はサポートしないため、
// 旧 @xeger/quill-image-formats が担っていた float / margin / display の保持を自前で行う。
// margin は無制限だと任意 CSS で表示崩れ / CSS 注入の温床になるため、企業担当者 → 受診者で
// 蓄積型 XSS 経路にならないよう許容値を限定する。
//
// parchment 3 の必要 API（Scope.INLINE と StyleAttributor コンストラクタ）に
// 限定した型で受けることで `any` を回避する。
type ParchmentApi = {
  Scope: { INLINE: number };
  StyleAttributor: new (
    attrName: string,
    keyName: string,
    options: { scope: number; whitelist?: string[] }
  ) => { attrName: string; keyName: string; whitelist?: string[] };
};
const Parchment = Quill.import('parchment') as unknown as ParchmentApi;
const IMAGE_STYLE_SCOPE = Parchment.Scope.INLINE;

// whitelist の選定方針:
//   - 現行リサイザ (quill-resize-image) の toolbar から出力されうる値
//   - 旧 @xeger/quill-image-formats 時代に保存された既存 DB データの margin 値（`0 auto` / `0.5em` 等）との互換
//   - parchment v3 の canAdd は完全一致なので、想定値だけを過不足なく列挙する
const IMAGE_STYLE_ATTRS: ReadonlyArray<{ name: string; whitelist?: string[] }> = [
  { name: 'float', whitelist: ['left', 'right', 'inherit'] },
  { name: 'margin', whitelist: ['0', 'auto', '0 auto', '0.5em', '1em', '0.5em auto', '1em auto'] },
  { name: 'display', whitelist: ['inline', 'inline-block', 'block'] },
];
const imageStyleAttributors = IMAGE_STYLE_ATTRS.map(
  ({ name, whitelist }) =>
    new Parchment.StyleAttributor(name, name, { scope: IMAGE_STYLE_SCOPE, whitelist })
);

type CustomLinkValue = { url: string; text: string };

// クラス定義自体は副作用を持たない。Quill.register 呼び出しは
// _registerQuillModules 内でのみ行い、副作用を冪等化する。

// XSS 防止: javascript: 等の擬似プロトコルを弾く（http/https/mailto/tel/相対パス/アンカーのみ許容）
const SAFE_URL_RE = /^(https?:|mailto:|tel:|\/|#)/i;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class CustomLinkBlot extends (Embed as any) {
  static blotName = 'customLink';
  static tagName = 'span';
  static className = 'ql-button';

  static create(value: CustomLinkValue): HTMLElement {
    const node = document.createElement('span');
    node.setAttribute('class', 'ql-button');
    const link = document.createElement('a');
    const safeUrl = SAFE_URL_RE.test(value.url) ? value.url : '#';
    link.setAttribute('href', safeUrl);
    link.setAttribute('target', '_blank');
    // tabnabbing 防止
    link.setAttribute('rel', 'noopener noreferrer');
    link.textContent = value.text;
    node.appendChild(link);
    return node;
  }

  static value(node: HTMLElement): CustomLinkValue {
    const link = node.querySelector('a');
    if (link) {
      return {
        url: link.getAttribute('href') ?? '',
        text: link.textContent ?? '',
      };
    }
    return { url: '', text: '' };
  }
}

/**
 * Quill モジュールとカスタム Blot を登録する。
 * isQuillModulesRegistered フラグで 2 回目以降の呼び出しをスキップする（冪等化）。
 */
function _registerQuillModules(): void {
  if (isQuillModulesRegistered) return;
  isQuillModulesRegistered = true;

  // 画像リサイズモジュール（Quill 2.x 対応）
  Quill.register('modules/resize', QuillResizeImage);

  // 画像 style 保持の StyleAttributor 群。
  // 第 2 引数 true は overwrite フラグ：既存の同名 style attributor を上書き登録する
  // （attributor の内部キーは name 衝突しやすく、Quill 2 ビルトインや他モジュールとの
  // 衝突可能性に備えて明示的に上書きする）。
  // Quill 2 の register は attributor インスタンスを受けるオーバーロードを持つが、
  // 型定義が `(path: string, def: unknown) => void` 系の string オーバーロードを最初に
  // 解決するため、attributor オブジェクトを渡すには once-only な assertion が必要。
  for (const attr of imageStyleAttributors) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Quill.register as (def: any, overwrite?: boolean) => void)(attr, true);
  }

  // カスタムボタン Blot 登録
  Quill.register(CustomLinkBlot);
}

/**
 * Quillエディタコンポーネント
 */
export default function QuillEditor(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  toolbar?: ('link' | 'button' | 'image' | 'highlight')[];
  readOnly?: boolean;
  className?: string;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  // コンポーネント初回マウント時にモジュールを登録（フラグで二重登録防止）
  _registerQuillModules();

  const [isImageModalOpenState, setIsImageModalOpenState] = useState(false);
  const [isButtonModalOpenState, setIsButtonModalOpenState] = useState(false);
  // Quillエディタの参照
  const editorRef = useRef<ReactQuill | null>(null);
  // ツールバーに割り当てるUID
  const uuid = getUid(14);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * カンパニー画像選択モーダルを開きます。
   */
  const openCompanyImageModal = () => {
    setIsImageModalOpenState(true);
  };

  /**
   * カスタムボタン設定モーダルを開きます。
   */
  const openButtonModal = () => {
    setIsButtonModalOpenState(true);
  };

  /**
   * カンパニー画像を挿入します。
   */
  const onInsertCompanyImage = async (imageKey: string) => {
    if (!imageKey) return;
    const editor = editorRef.current?.getEditor();
    if (!editor) {
      console.error('QuillEditor: editor reference is not available');
      return;
    }
    const url = getCompanyImageUrl(imageKey);
    const range = editor.getSelection();
    editor.insertEmbed(range?.index ?? 0, 'image', url);
  };

  /**
   * カスタムボタンを挿入します。
   */
  const onInsertButton = async (buttonText: string, buttonUrl: string) => {
    if (!buttonUrl || !buttonText) return;
    const editor = editorRef.current?.getEditor();
    if (!editor) {
      console.error('QuillEditor: editor reference is not available');
      return;
    }
    const range = editor.getSelection(true);
    editor.insertEmbed(range.index, 'customLink', { url: buttonUrl, text: buttonText }, 'user');
    editor.setSelection(range.index + 1, 'silent');
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */

  // Quillエディタのモジュール設定
  // uuid / ハンドラはコンポーネント初期化時に確定するため空依存配列で意図的に固定する
  const modules = useMemo(
    () => ({
      toolbar: {
        container: '#toolbar-' + uuid,
        handlers: {
          companyImage: openCompanyImageModal,
          linkButton: openButtonModal,
        },
      },
      clipboard: {
        matchVisual: false,
      },
      resize: {
        locale: {},
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <div className={`quill-editor ${props.readOnly ? 'readonly' : ''} ${props.className || ''}`}>
        <div id={`toolbar-${uuid}`}>
          <span className="ql-formats">
            <button className="ql-bold" />
            {props.toolbar?.includes('link') && <button className="ql-link" />}
            {props.toolbar?.includes('highlight') && <select className="ql-background" />}
            {props.toolbar?.includes('image') && (
              <button className="ql-companyImage" type="button">
                <svg viewBox="0 0 18 18">
                  <rect className="ql-stroke" height="10" width="12" x="3" y="4"></rect>
                  <circle className="ql-fill" cx="6" cy="7" r="1"></circle>{' '}
                  <polyline
                    className="ql-even ql-fill"
                    points="5 12 5 11 7 9 8 10 11 7 13 9 13 12 5 12"
                  ></polyline>
                </svg>
              </button>
            )}
            {props.toolbar?.includes('button') && (
              <button className="ql-linkButton" type="button">
                <svg width="18" height="18" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" fill="none" width="20" height="20" />

                  <g>
                    <path d="M17 5H3c-1.1 0-2 .9-2 2v6c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm1 7c0 .6-.4 1-1 1H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h14c.6 0 1 .4 1 1v5z" />
                  </g>
                </svg>
              </button>
            )}
          </span>
        </div>
        <ReactQuill
          ref={editorRef}
          modules={modules}
          theme="snow"
          value={props.value}
          onChange={props.onChange}
          placeholder={props.placeholder}
          readOnly={props.readOnly}
        />
      </div>
      {isImageModalOpenState && (
        <ModalCompanyImageGallery
          setOpen={setIsImageModalOpenState}
          open={isImageModalOpenState}
          onImageSelected={onInsertCompanyImage}
        />
      )}
      <QuillModalButton
        setOpen={setIsButtonModalOpenState}
        open={isButtonModalOpenState}
        onSubmit={onInsertButton}
      />
    </>
  );
}
